import cx from 'classnames'
import { Link as GatsbyLink, GatsbyLinkProps } from 'gatsby'
import React from 'react'
import { buildTagDataAttr } from '@cinch/oxd-utils/analytics/src'

import * as styles from './link.module.css'

import * as baseButtonStyles from '@cinch/oxd-components/button/src/base/base-button.module.css'

import * as primaryStyles from '@cinch/oxd-components/button/src/primary/primary-button.module.css'

import * as outlineStyles from '@cinch/oxd-components/button/src/outline/outline.module.css'

import * as tertiaryStyles from '@cinch/oxd-components/button/src/tertiary/tertiary.module.css'

import * as outlineMonoStyles from '@cinch/oxd-components/button/src/outline-mono/outline-mono.module.css'

export enum Appearance {
  primaryButton = 'primary-button',
  outlineButton = 'outline-button',
  tertiaryButton = 'tertiary-button',
  outlineMonoButton = 'outline-mono-button',
}

export enum Size {
  mini = 'mini',
  regular = 'regular',
  large = 'large',
}

export interface LinkProps extends GatsbyLinkProps<any> {
  className?: string
  appearance?: Appearance
  size?: Size
  external?: boolean
  isDisabled?: boolean
  tag?: Cinch.AnalyticsTag
}

const isButtonType = (appearance: Appearance) => {
  return (
    appearance === Appearance.primaryButton ||
    appearance === Appearance.outlineButton ||
    appearance === Appearance.tertiaryButton ||
    appearance === Appearance.outlineMonoButton
  )
}

const Link: React.FC<LinkProps> = ({
  className,
  appearance,
  size,
  to,
  external,
  tag,
  activeClassName,
  isDisabled,
  children,
  ...rest
}) => {
  const classNames = cx(styles.link, className, {
    [baseButtonStyles.base]: isButtonType(appearance!),
    [baseButtonStyles.mini]: isButtonType(appearance!) && size === Size.mini,
    [baseButtonStyles.regular]: isButtonType(appearance!) && (!size || size === Size.regular),
    [baseButtonStyles.large]: isButtonType(appearance!) && size === Size.large,
    [primaryStyles.button]: appearance === Appearance.primaryButton,
    [outlineStyles.button]: appearance === Appearance.outlineButton,
    [outlineStyles.disabled]: appearance === Appearance.outlineButton && isDisabled,
    [tertiaryStyles.button]: appearance === Appearance.tertiaryButton,
    [outlineMonoStyles.button]: appearance === Appearance.outlineMonoButton,
  })

  const isPartiallyActive = ({ isPartiallyCurrent }: { isPartiallyCurrent: boolean }) => {
    return isPartiallyCurrent && activeClassName ? { className: cx(classNames, activeClassName) } : null
  }

  const analyticsDataAttributes = buildTagDataAttr(tag)

  const restProps = {
    ...rest,
    ...analyticsDataAttributes,
  }

  if (isDisabled) {
    restProps.onClick = (event) => event.preventDefault()
  }

  return external ? (
    <a href={to} className={classNames} disabled={isDisabled} {...(restProps as any)}>
      {children}
    </a>
  ) : (
    <GatsbyLink
      to={to}
      getProps={isPartiallyActive}
      disabled={isDisabled}
      className={classNames}
      {...(restProps as any)}
    >
      {!external && appearance === Appearance.outlineButton && <span>{children}</span>}
      {external || (appearance !== Appearance.outlineButton && children)}
    </GatsbyLink>
  )
}

export default Link
